// Ported from design/project/App12-Versus.dc.html, then wired to the matched battle.
import { useEffect, useRef } from "react";
import { cardInitials, displayName, payoutFor, record, useGame, usePlayer } from "../game.js";
import { HeadToHead, leadNote, useRival } from "./Rivalry.js";
import { go } from "../router.js";
import { play } from "../sound.js";

const MATCHED_FLASH_MS = 1_600;

export function App12_Versus() {
  const { battle, mySide } = useGame();
  const long = usePlayer(battle?.long);
  const short = usePlayer(battle?.short);
  const opp = mySide === "long" ? short : long;
  const rival = useRival(battle && mySide ? battle[mySide === "long" ? "short" : "long"] : null);
  // Played before: this battle is the next round of a rivalry, and the score shows under VS.
  const rivalry = rival && rival.rounds > 0 ? rival : null;
  // A flash, not a wait: the battle clock is already running, so this shows who you're up against and moves on.
  const played = useRef(false);
  useEffect(() => {
    if (!played.current) play("matched"); // once, even when React runs this twice in development
    played.current = true;
    const t = setTimeout(() => go("App14", true), MATCHED_FLASH_MS);
    return () => clearTimeout(t);
  }, []);
  if (!battle) return null;
  const { pot, payout } = payoutFor(battle.stake, battle.feeBps ?? 400);
  const tag = (c: typeof long) => (c?.username ?? c?.firstName ?? "...").toUpperCase();
  return (
    <>
      <div className="screen">
  <div className="htL" aria-hidden="true" style={{ position: "absolute", inset: "0", clipPath: "polygon(0 0, 100% 0, 100% 30%, 0 58%)" }}></div>
  <div className="htR" aria-hidden="true" style={{ position: "absolute", inset: "0", clipPath: "polygon(100% 30%, 100% 100%, 0 100%, 0 58%)" }}></div>
  <div aria-hidden="true" style={{ position: "absolute", inset: "0", background: "linear-gradient(180deg, rgba(11,14,20,0) 50%, #0B0E14 88%)" }}></div>
  <svg aria-hidden="true" width="390" height="844" viewBox="0 0 390 844" style={{ position: "absolute", left: "0", top: "0" }}>
    <line x1="0" y1="490" x2="390" y2="253" stroke="#FFFFFF" strokeWidth="4"></line>
  </svg>

  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "30px" }}>
    <div style={{ display: "flex", justifyContent: "center" }}>
      <span className="tag tagW" style={{ height: "32px", fontSize: "12px" }}>{rivalry ? `RIVALRY · ROUND ${rivalry.rounds + 1}` : "MATCH FOUND"}</span>
    </div>

    <div style={{ marginTop: "26px", display: "flex", alignItems: "center", gap: "14px" }}>
      <div className="avA" style={{ flex: "none", width: "92px", height: "92px", borderRadius: "22px", borderWidth: "3px", background: "#CCFF00", fontSize: "26px", boxShadow: "6px 6px 0 #0B0E14", ...(long?.frame && { backgroundImage: `linear-gradient(#CCFF00, #CCFF00), ${long.frame}`, backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box", border: "4px solid transparent" }) }}>{cardInitials(long)}</div>
      <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column", gap: "6px" }}>
        <span className="unb" style={{ fontWeight: "900", fontSize: "20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tag(long)}</span>
        <span className="tag tagL" style={{ alignSelf: "flex-start" }}>▲ LONG</span>
        <span className="mono muted" style={{ fontSize: "12px", fontWeight: "700" }}>{record(long)}</span>
      </div>
    </div>

    <div style={{ marginTop: rivalry ? "14px" : "26px", alignSelf: "center", position: "relative" }}>
      <svg width={rivalry ? 120 : 170} height={rivalry ? 84 : 120} viewBox="0 0 170 120" role="img" aria-label="VS">
        <path d="M96 2 L54 60 H80 L66 118 L122 46 H92 Z" fill="#FFFFFF" stroke="#0B0E14" strokeWidth="4" strokeLinejoin="round" style={{ animation: "ddBolt 0.8s infinite", transformOrigin: "85px 60px" }}></path>
        <text x="85" y="78" textAnchor="middle" fontFamily="Unbounded" fontWeight="900" fontSize="54" fill="#0B0E14" stroke="#0B0E14" strokeWidth="10" strokeLinejoin="round">VS</text>
        <text x="85" y="78" textAnchor="middle" fontFamily="Unbounded" fontWeight="900" fontSize="54" fill="#FFFFFF">VS</text>
      </svg>
    </div>
    {rivalry && (
      <div style={{ marginTop: "6px", alignSelf: "center", maxWidth: "100%", boxSizing: "border-box", padding: "10px 18px", borderRadius: "16px", background: "rgba(11,14,20,0.82)", border: "2px solid #30363D" }}>
        <HeadToHead them={displayName(opp, "THEM")} mine={rivalry.wins} theirs={rivalry.losses} note={leadNote(rivalry.wins, rivalry.losses)} />
      </div>
    )}

    <div style={{ marginTop: rivalry ? "14px" : "26px", display: "flex", alignItems: "center", gap: "14px", flexDirection: "row-reverse", textAlign: "right" }}>
      <div className="avA" style={{ flex: "none", width: "92px", height: "92px", borderRadius: "22px", borderWidth: "3px", background: "#FF3366", fontSize: "26px", boxShadow: "-6px 6px 0 #0B0E14", ...(short?.frame && { backgroundImage: `linear-gradient(#FF3366, #FF3366), ${short.frame}`, backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box", border: "4px solid transparent" }) }}>{cardInitials(short)}</div>
      <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
        <span className="unb" style={{ maxWidth: "100%", fontWeight: "900", fontSize: "20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tag(short)}</span>
        <span className="tag tagR">SHORT ▼</span>
        <span className="mono muted" style={{ fontSize: "12px", fontWeight: "700" }}>{record(short)}</span>
      </div>
    </div>

    <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>
      <div className="panel" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", padding: "12px 0" }}>
        <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>ASSET</div><div className="mono" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{battle.arena === "XAU" ? "GOLD" : battle.arena}</div></div>
        <div style={{ textAlign: "center", borderLeft: "2px solid #30363D", borderRight: "2px solid #30363D" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>POT</div><div className="mono" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{pot}</div></div>
        <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>WINNER GETS</div><div className="mono lime" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{payout}</div></div>
      </div>
      <div className="btnA" role="status">MATCHED!</div>
      <span className="muted" style={{ textAlign: "center", fontSize: "11px" }}>Stakes locked. The start price is set. {battle.durationSec === 60 ? "1 minute" : "3 minutes"} on the clock.</span>
    </div>
  </div>
</div>
    </>
  );
}
